/**
 * Aigentico corporate mark — circular badge with the pierced-A glyph.
 * Faithful SVG rebuild of the printed logo: two concentric rings (navy
 * outer, red inner), a tall geometric slab-serif A in navy, a red bar
 * piercing horizontally through the A, terminating on the right in a
 * small red dot. Renders crisp from 16px up to any size.
 *
 * When rendered inside a link, the parent should point to https://aigentico.ai.
 */
export function AigenticoBadge({ size = 32, className = "" }: { size?: number; className?: string }) {
  const NAVY = "#001464";
  const RED  = "#C80028";
  const s = size;

  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Aigentico"
      className={className}
    >
      {/* Outer navy ring */}
      <circle cx="50" cy="50" r="47" fill="none" stroke={NAVY} strokeWidth="3" />
      {/* Inner red ring (thin, close to outer with a whisper of white between) */}
      <circle cx="50" cy="50" r="41.5" fill="none" stroke={RED} strokeWidth="1.8" />

      {/* The letter A: single unified silhouette with a sharp apex, two legs
          fanning to feet, and serifs on each foot. Drawn as filled polygons
          for crispness at any size. */}
      <g fill={NAVY}>
        {/* A body: apex at (50,22), fans out to feet, inner triangle removed
            by the polygon path itself. */}
        <polygon points="50,22 33,74 41,74 50,32 59,74 67,74" />
        {/* Left foot serif */}
        <rect x="26" y="72" width="15" height="4" />
        {/* Right foot serif */}
        <rect x="59" y="72" width="15" height="4" />
      </g>

      {/* Red horizontal bar piercing through the A at crossbar height.
          Extends past both legs. */}
      <rect x="19" y="53" width="60" height="6" fill={RED} />
      {/* Red terminating dot on the right end of the bar. */}
      <circle cx="83" cy="56" r="4.5" fill={RED} />
    </svg>
  );
}

/**
 * Combined "An Aigentico product" strip — used on landing / marketing pages.
 * On mobile it collapses to just the badge.
 */
export function AigenticoWordmark({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <AigenticoBadge size={20} />
      <span className="text-xs uppercase tracking-widest text-ink-soft font-medium">
        an <span className="text-aigentico-navy">Aigentico</span> product
      </span>
    </span>
  );
}
